'use client';

import { useMemo, useState } from 'react';
import {
  EXTRA_UNIT_CONFIG,
  formatExtraNumber,
  type DigitalBase,
  type ExtraUnitVariant,
} from '@/lib/browser-extra-units-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserExtraUnitConverter({ variant }: { variant: ExtraUnitVariant }) {
  const config = EXTRA_UNIT_CONFIG[variant];
  const [rawValue, setRawValue] = useState<string>(() => String(config.sampleValue));
  const [fromUnit, setFromUnit] = useState<string>(config.defaultFrom);
  const [toUnit, setToUnit] = useState<string>(config.defaultTo);
  const [baseMode, setBaseMode] = useState<DigitalBase>('decimal');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const trimmed = rawValue.trim();
  const parsed = trimmed === '' ? Number.NaN : Number(trimmed);
  const isValid = trimmed !== '' && Number.isFinite(parsed);

  const rows = useMemo(() => {
    if (!isValid) return [];
    return config.units.map((unit) => ({
      unit,
      value: config.convert(parsed, fromUnit, unit.id, baseMode),
    }));
  }, [config, isValid, parsed, fromUnit, baseMode]);

  const primaryResult = useMemo(() => {
    if (!isValid) return Number.NaN;
    return config.convert(parsed, fromUnit, toUnit, baseMode);
  }, [config, isValid, parsed, fromUnit, toUnit, baseMode]);

  const fromSymbol = config.units.find((u) => u.id === fromUnit)?.symbol ?? '';
  const toSymbol = config.units.find((u) => u.id === toUnit)?.symbol ?? '';

  const resultLine = isValid
    ? `${formatExtraNumber(parsed)} ${fromSymbol} = ${formatExtraNumber(primaryResult)} ${toSymbol}`
    : '';

  async function handleCopy() {
    if (!resultLine) return;
    try {
      await copyText(resultLine);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  function handleSwap() {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{config.badge}</span>
        <h2 className="tool-live-title">{config.heading}</h2>
        <p className="tool-live-subtitle">{config.subtitle}</p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Value</span>
              <input
                inputMode="decimal"
                onChange={(e) => setRawValue(e.target.value)}
                placeholder="Enter a number"
                type="text"
                value={rawValue}
              />
            </label>
            <label className="tool-live-field">
              <span>From</span>
              <select onChange={(e) => setFromUnit(e.target.value)} value={fromUnit}>
                {config.units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name} ({unit.symbol})
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>To</span>
              <select onChange={(e) => setToUnit(e.target.value)} value={toUnit}>
                {config.units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name} ({unit.symbol})
                  </option>
                ))}
              </select>
            </label>
            {config.hasBaseToggle ? (
              <label className="tool-live-field">
                <span>Base</span>
                <select
                  onChange={(e) => setBaseMode(e.target.value as DigitalBase)}
                  value={baseMode}
                >
                  <option value="decimal">Decimal — 1 KB = 1000 B (SI)</option>
                  <option value="binary">Binary — 1 KiB = 1024 B</option>
                </select>
              </label>
            ) : null}
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleSwap} type="button">
              Swap units
            </button>
            <button
              className="button button-secondary"
              disabled={!isValid}
              onClick={handleCopy}
              type="button"
            >
              Copy result
            </button>
          </div>
          {!isValid && trimmed !== '' ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              Enter a valid number to convert.
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div className="tool-live-field tool-live-field-full">
            <span>Result</span>
            <p
              style={{
                margin: '0.35rem 0 0',
                fontSize: '1.25rem',
                fontWeight: 600,
                minHeight: '1.5rem',
              }}
            >
              {isValid ? resultLine : '—'}
            </p>
          </div>
          <div className="tool-live-field tool-live-field-full">
            <span>All units</span>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '0.35rem 0.5rem' }}>Unit</th>
                    <th style={{ textAlign: 'right', padding: '0.35rem 0.5rem' }}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={2} style={{ padding: '0.5rem', opacity: 0.7 }}>
                        Enter a value to see every unit.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr
                        key={row.unit.id}
                        style={{
                          fontWeight: row.unit.id === toUnit ? 600 : 400,
                          borderTop: '1px solid rgba(148, 163, 184, 0.25)',
                        }}
                      >
                        <td style={{ padding: '0.35rem 0.5rem' }}>
                          {row.unit.name} ({row.unit.symbol})
                        </td>
                        <td
                          style={{
                            padding: '0.35rem 0.5rem',
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {formatExtraNumber(row.value)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <p className="tool-live-panel-note">Converted locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
